import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Props for StatsCard component
 */
interface StatsCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  trend?: {
    value: number;
    label: string;
    direction?: 'up' | 'down' | 'flat';
  };
  subtitle?: string;
  clickable?: boolean;
  onClick?: () => void;
  navigateTo?: string;
  tooltip?: string;
  badge?: {
    label: string;
    color?: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  };
}

/**
 * StatsCard Component
 * Displays a statistic with icon, value, and optional trend indicator
 *
 * Used in admin dashboard to show key metrics like:
 * - Total users
 * - Pending approvals
 * - Content stats
 * - System health
 */
const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  color = 'primary',
  trend,
  subtitle,
  clickable = false,
  onClick,
  navigateTo,
  tooltip,
  badge,
}) => {
  const navigate = useNavigate();

  /**
   * Handle card click
   */
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (navigateTo) {
      navigate(navigateTo);
    }
  };

  /**
   * Get trend icon based on direction
   */
  const getTrendIcon = () => {
    if (!trend) return null;

    const direction = trend.direction || 'flat';
    const trendColor =
      direction === 'up'
        ? 'success.main'
        : direction === 'down'
        ? 'error.main'
        : 'text.secondary';

    const TrendIcon =
      direction === 'up'
        ? TrendingUpIcon
        : direction === 'down'
        ? TrendingDownIcon
        : TrendingFlatIcon;

    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <TrendIcon sx={{ fontSize: 16, color: trendColor }} />
        <Typography variant="caption" sx={{ color: trendColor, fontWeight: 600 }}>
          {trend.value > 0 && direction === 'up' ? '+' : ''}
          {trend.value}
          {typeof trend.value === 'number' && '%'}
        </Typography>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        height: '100%',
        cursor: clickable || onClick || navigateTo ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': {
          ...(clickable || onClick || navigateTo
            ? {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              }
            : {}),
        },
      }}
      onClick={handleClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Avatar
            sx={{
              bgcolor: `${color}.main`,
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>

          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
            {badge && (
              <Chip
                label={badge.label}
                color={badge.color || 'default'}
                size="small"
                sx={{ height: 20 }}
              />
            )}
            {tooltip && (
              <Tooltip title={tooltip} arrow>
                <IconButton size="small" sx={{ padding: 0 }}>
                  <InfoIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Typography variant="h4" component="div" gutterBottom fontWeight={700}>
          {value}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>

        {(trend || subtitle) && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
            pt={1}
            borderTop={1}
            borderColor="divider"
          >
            {trend && (
              <Box display="flex" flexDirection="column">
                {getTrendIcon()}
                <Typography variant="caption" color="text.secondary">
                  {trend.label}
                </Typography>
              </Box>
            )}

            {subtitle && !trend && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StatsCard;
